import { useEffect } from "react";
import { CheckCircle, TrendingUp, BarChart2, Home, ArrowRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useMeta, useSchema } from "@/hooks/useMeta";

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Marktwertermittlung Kreis Mettmann",
  provider: { "@type": "RealEstateAgent", name: "BRIMO Immobilien NRW" },
  areaServed: "Kreis Mettmann",
  description: "Professionelle und kostenlose Marktwertermittlung für Immobilien im Kreis Mettmann.",
};

export default function MarktWertPage() {
  useMeta(
    "Marktwertermittlung Kreis Mettmann | BRIMO Immobilien",
    "Kostenlose Marktwertermittlung für Ihre Immobilie im Kreis Mettmann. BRIMO Immobilien – fundierte Bewertung auf Basis aktueller Marktdaten. Jetzt anfragen."
  );
  useSchema(schema);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-end pb-16" data-testid="valuation-hero">
        <div className="absolute inset-0">
          <img
            src={cityConfig.images.valuationHero}
            alt="Marktwertermittlung Kreis Mettmann"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-[#0d0d0d]/90" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-6 max-w-7xl pt-28">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <p className="text-gray-300 text-xs uppercase tracking-[0.3em] mb-4">
            Kostenlos & unverbindlich
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight leading-none mb-5"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            MARKTWERT IHRER
            <br />
            <span className="text-[#E61D38]">IMMOBILIE</span>
            <br />
            IM KREIS METTMANN
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
            Erfahren Sie kostenlos und unverbindlich, was Ihre Immobilie im Kreis Mettmann heute wert
            ist – basierend auf aktuellen Marktdaten und lokaler Expertise.
          </p>
        </div>
      </section>

      {/* Main Content + Form */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="valuation-main">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Info */}
            <div className="lg:col-span-3">
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-3xl md:text-4xl font-black text-white uppercase tracking-tight mb-6"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                WARUM EINE PROFESSIONELLE BEWERTUNG?
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Eine fundierte Immobilienbewertung ist die Grundlage jedes erfolgreichen Verkaufs.
                Zu hoch angesetzt schreckt potenzielle Käufer ab und verlängert die
                Vermarktungszeit erheblich. Zu niedrig bedeutet finanziellen Verlust.
              </p>
              <p className="text-gray-400 leading-relaxed mb-10">
                Nur eine marktgerechte Preisgestaltung führt zu einem schnellen Verkauf zum
                optimalen Preis. BRIMO Immobilien NRW ermittelt den realen Marktwert Ihrer
                Immobilie auf Basis aktueller Vergleichsdaten und jahrelanger lokaler Erfahrung.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
                {[
                  {
                    icon: <TrendingUp size={28} className="text-[#E61D38]" />,
                    title: "Aktuell",
                    desc: "Auf Basis aktueller Verkaufspreise im Markt im Kreis Mettmann",
                  },
                  {
                    icon: <BarChart2 size={28} className="text-[#E61D38]" />,
                    title: "Fundiert",
                    desc: "Professionelle Bewertungsverfahren nach Immobilienwertermittlungsverordnung",
                  },
                  {
                    icon: <Home size={28} className="text-[#E61D38]" />,
                    title: "Lokal",
                    desc: "Tiefekenntnis aller Städte im Kreis Mettmann und Mikrolagen",
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-[#262626] border border-white/5 p-6 hover:border-[#E61D38]/40 transition-colors"
                    data-testid={`valuation-benefit-${i}`}
                  >
                    <div className="mb-4">{item.icon}</div>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* What's included */}
              <div className="bg-[#0d0d0d] border border-white/5 p-8">
                <h3
                  className="text-white font-bold text-sm uppercase tracking-wider mb-6"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Was beinhaltet unsere Marktwertermittlung?
                </h3>
                <ul className="space-y-4">
                  {[
                    "Persönliche Besichtigung und Zustandsanalyse",
                    "Vergleich mit aktuellen Verkaufspreisen in der Stadt",
                    "Anwendung anerkannter Bewertungsverfahren",
                    "Schriftliche Zusammenfassung des Marktwertes",
                    "Empfehlung zur optimalen Preisstrategie",
                    "Unverbindliche Erstberatung zum Verkaufsprozess",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={14} className="text-[#E61D38] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Red CTA banner */}
              <div className="mt-8 bg-[#E61D38] p-6 flex items-center justify-between gap-6">
                <div>
                  <p className="text-white font-bold text-sm uppercase tracking-wider">
                    Kostenlos & unverbindlich
                  </p>
                  <p className="text-white/80 text-xs mt-1">
                    Erfahren Sie den Wert Ihrer Immobilie – ohne Kosten, ohne Verpflichtung.
                  </p>
                </div>
                <ArrowRight className="text-white flex-shrink-0" size={24} />
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="sticky top-24">
                <LeadForm
                  formType="market_value"
                  title="Marktwert ermitteln"
                  ctaText="Marktwert ermitteln lassen"
                  showDistrict={true}
                  showPropertyType={true}
                  pageSource="/marktwertermittlung-kreis-mettmann"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-12 bg-[#0d0d0d] border-y border-white/5" data-testid="valuation-trust">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { label: "Städte im Kreis Mettmann", value: "10" },
              { label: "Erfahrung vor Ort", value: "Lokal" },
              { label: "Erstberatung", value: "Kostenlos" },
              { label: "Reaktionszeit", value: "< 24h" },
            ].map((item, i) => (
              <div key={i} data-testid={`valuation-stat-${i}`}>
                <p
                  className="text-[#E61D38] font-black text-2xl md:text-3xl mb-2"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  {item.value}
                </p>
                <p className="text-gray-400 text-xs uppercase tracking-wider">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
